import { el } from '../lib/dom.js';
import { prettify } from '../data/pokedex-resolver.js';

const clean = (s) => s.replace(/\s+/g, ' ').trim();

function label(names) {
  const arr = [...names].map(prettify);
  return arr.length <= 3 ? arr.join(', ') : `${arr.slice(0, 3).join(', ')} +${arr.length - 3}`;
}

/**
 * Pokédex flavour text. The primary block shows the selected game's
 * entry/entries (with a Sword/Shield-style pill toggle when they differ);
 * an "All Pokédex entries" disclosure lists every distinct entry across
 * every game, newest first.
 */
export function FlavorText(species, vg) {
  const english = species.flavor_text_entries.filter((f) => f.language.name === 'en');
  if (!english.length) return null;

  // distinct text -> set of version names, in newest-first order
  const order = [];
  const versionsByText = new Map();
  for (let i = english.length - 1; i >= 0; i--) {
    const text = clean(english[i].flavor_text);
    if (!versionsByText.has(text)) {
      versionsByText.set(text, new Set());
      order.push(text);
    }
    versionsByText.get(text).add(english[i].version.name);
  }

  // The selected game's distinct entries.
  const wanted = vg ? vg.versions : [];
  let primary = [];
  for (const v of wanted) {
    const hit = english.find((e) => e.version.name === v);
    if (!hit) continue;
    const text = clean(hit.flavor_text);
    if (!primary.some((p) => p.text === text)) primary.push({ version: v, text });
  }
  let fallback = false;
  if (!primary.length) {
    primary = [{ version: [...versionsByText.get(order[0])][0], text: order[0] }];
    fallback = true;
  }

  const quote = el('p', { class: 'ppanel__flavor' });
  const caption = el('span', { class: 'flavor__cap' });
  const toggle = el('div', { class: 'flavor__toggle' });
  let index = 0;

  function paint() {
    const p = primary[index];
    quote.textContent = p.text;
    quote.style.animation = 'none';
    quote.offsetWidth; // reflow so the slide-in re-runs
    quote.style.animation = '';
    caption.textContent = fallback
      ? `— ${prettify(p.version)} (most recent)`
      : `— Pokémon ${prettify(p.version)}`;
    [...toggle.children].forEach((b, i) => b.classList.toggle('is-active', i === index));
  }

  if (primary.length > 1) {
    primary.forEach((p, i) =>
      toggle.append(
        el(
          'button',
          {
            class: 'flavor__pill',
            type: 'button',
            onclick: () => {
              index = i;
              paint();
            },
          },
          prettify(p.version),
        ),
      ),
    );
  }
  paint();

  const allEntries =
    order.length > 1
      ? el(
          'details',
          { class: 'flavor__all' },
          el('summary', {}, `All Pokédex entries (${order.length})`),
          el(
            'ul',
            { class: 'flavor__list' },
            ...order.map((text) =>
              el(
                'li',
                {},
                el('span', { class: 'flavor__list-ver' }, label(versionsByText.get(text))),
                el('span', {}, text),
              ),
            ),
          ),
        )
      : null;

  return el(
    'div',
    { class: 'flavor' },
    primary.length > 1 ? toggle : null,
    quote,
    caption,
    allEntries,
  );
}
